// Help centre on the web: searchable answers, contact support (opens a ticket that Fable AI answers first in Messages),
// report a bug, and your past requests.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bug, ChevronDown, LifeBuoy, MessageCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const TOPICS = [
  { t: "Bookings", qs: [
    ["How do I change or cancel a booking?", "Open Bookings, pick the booking and choose Reschedule or Cancel. What you get back depends on the provider's cancellation policy, which is shown before you pay and on the booking itself."],
    ["My provider didn't turn up", "Open the booking and choose Report a problem, or contact us below. We'll look into it and, where the provider is at fault, arrange a refund."],
    ["Can I message a provider before booking?", "Yes. Use Message on their listing. Keep payments on Fable+ so you're covered if something goes wrong."],
  ] },
  { t: "Payments & refunds", qs: [
    ["When am I charged?", "Card payments are taken securely by Stripe when you confirm. Some bookings are authorised first and only captured once the service is confirmed."],
    ["How long do refunds take?", "Refunds go back to the card you paid with. Banks usually show them within 5–10 working days."],
    ["Where are my receipts?", "Every booking, order and ticket has a receipt in its detail page, and we email a copy when you pay."],
  ] },
  { t: "Events & tickets", qs: [
    ["Where are my tickets?", "Open Tickets. Show the QR code at the door — it works offline once loaded."],
    ["Can I transfer or cancel a ticket?", "Open the ticket and choose Transfer, or Cancel if the event's refund window is still open."],
    ["How do I host an event?", "Go to Host an event, add the date, place and ticket types, then publish. Paid tickets need payouts set up first."],
  ] },
  { t: "Account & security", qs: [
    ["How do I turn on two-step sign-in?", "Settings → Sign-in & devices → Two-step sign-in. We'll send a code whenever you sign in on a new device."],
    ["I've lost access to my account", "Use Forgot password on the sign-in page. If you no longer have access to your email, contact us below from any account or email hello@fableplus.uk."],
    ["How do I download or delete my data?", "Settings → Your data lets you download everything we hold (UK GDPR). Settings → Delete account schedules deletion with a grace period to change your mind."],
  ] },
  { t: "Safety & reporting", qs: [
    ["How do I report someone or something?", "Use Report on any profile, listing, post, message or event. Reports are private — the person isn't told who reported them."],
    ["How do I block someone?", "Open their profile or chat and choose Block. You can review blocked people in Settings → Privacy."],
    ["Is there an emergency?", "If anyone is in immediate danger, call 999 (or 112). Then let us know so we can act on the account."],
  ] },
  { t: "Providers & creators", qs: [
    ["How do I start offering services?", "Open the partner area, follow the steps to add your business, verify your identity and set up payouts."],
    ["When do I get paid?", "Payouts go to your bank through Stripe once the service is complete, minus the fees shown in your earnings."],
  ] },
];

function Answer({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-0">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-4 py-3 text-left text-sm font-semibold">
        {q}<ChevronDown className={"h-4 w-4 shrink-0 transition " + (open ? "rotate-180" : "")} aria-hidden="true" />
      </button>
      {open ? <p className="pb-4 text-sm text-muted-foreground">{a}</p> : null}
    </div>
  );
}

export default function HelpCentre() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("ticket");
  const [f, setF] = useState({ subject: "", message: "", category: "general" });
  const [b, setB] = useState({ title: "", description: "", severity: "medium" });
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = "Help centre · Fable+"; }, []);
  useEffect(() => { if (user) api("/support/tickets").then(setMine).catch(() => setMine([])); }, [user]);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return TOPICS;
    return TOPICS.map((t) => ({ ...t, qs: t.qs.filter(([x, y]) => (x + " " + y).toLowerCase().includes(s)) })).filter((t) => t.qs.length);
  }, [q]);
  const need = () => { if (!user) { toast("Sign in to contact support"); nav("/login?next=/help"); return true; } return false; };
  const send = async (e) => {
    e.preventDefault(); if (need()) return;
    if (f.subject.trim().length < 3 || f.message.trim().length < 10) { toast.error("Add a subject and a few words about the problem"); return; }
    setBusy(true);
    try {
      const t = await api("/support/tickets", { method: "POST", body: f });
      toast.success("Sent — we've replied in Messages");
      setF({ subject: "", message: "", category: "general" }); setMine((m) => [t, ...m]);
      if (t.conversation_id) nav("/messages/" + t.conversation_id);
    } catch (err) { toast.error(err.message || "Couldn't send"); } finally { setBusy(false); }
  };
  const bug = async (e) => {
    e.preventDefault(); if (need()) return;
    if (b.title.trim().length < 3 || b.description.trim().length < 10) { toast.error("Tell us what happened"); return; }
    setBusy(true);
    try { await api("/support/bugs", { method: "POST", body: { ...b, screen: document.referrer || null, device: navigator.userAgent.slice(0, 180) } }); toast.success("Thanks — the team will look at it"); setB({ title: "", description: "", severity: "medium" }); }
    catch (err) { toast.error(err.message || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="help-centre">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">Help centre</p>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">How can we help?</h1>
      <label className="relative mt-6 block max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input className={inp + " pl-9 py-3"} placeholder="Search answers — refunds, tickets, two-step…" aria-label="Search help" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {hits.length === 0 ? <p className="text-sm text-muted-foreground">No answers match — contact us below.</p> : hits.map((t) => (
          <section key={t.t} className="rounded-2xl border p-5"><h2 className="mb-1 font-bold">{t.t}</h2>{t.qs.map(([x, y]) => <Answer key={x} q={x} a={y} />)}</section>
        ))}
      </div>
      <section className="mt-12 grid gap-6 md:grid-cols-[minmax(0,1fr)_300px]" id="contact">
        <div className="rounded-2xl border p-5">
          <div className="mb-4 flex gap-2" role="tablist">
            <Button type="button" role="tab" aria-selected={mode === "ticket"} variant={mode === "ticket" ? "default" : "outline"} onClick={() => setMode("ticket")}><MessageCircle className="mr-1 h-4 w-4" />Contact support</Button>
            <Button type="button" role="tab" aria-selected={mode === "bug"} variant={mode === "bug" ? "default" : "outline"} onClick={() => setMode("bug")}><Bug className="mr-1 h-4 w-4" />Report a bug</Button>
          </div>
          {mode === "ticket" ? (
            <form onSubmit={send} className="grid gap-3">
              <select className={inp} aria-label="Topic" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                <option value="general">General</option><option value="booking">A booking</option><option value="billing">Payments & refunds</option><option value="account">My account</option><option value="other">Something else</option>
              </select>
              <input className={inp} placeholder="Subject" aria-label="Subject" maxLength={140} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
              <textarea className={inp + " min-h-[140px]"} placeholder="What's happening? Include booking or order numbers if you have them." aria-label="Message" maxLength={4000} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
              <Button type="submit" disabled={busy} className="justify-self-end">Send</Button>
              <p className="text-xs text-muted-foreground">Fable AI replies straight away in Messages; a person from our team follows up when needed.</p>
            </form>
          ) : (
            <form onSubmit={bug} className="grid gap-3">
              <input className={inp} placeholder="What went wrong?" aria-label="Bug title" maxLength={140} value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} />
              <textarea className={inp + " min-h-[140px]"} placeholder="Steps to reproduce, what you expected and what happened" aria-label="Bug description" maxLength={4000} value={b.description} onChange={(e) => setB({ ...b, description: e.target.value })} />
              <select className={inp} aria-label="How bad is it" value={b.severity} onChange={(e) => setB({ ...b, severity: e.target.value })}>
                <option value="low">Minor annoyance</option><option value="medium">Gets in the way</option><option value="high">Can't use Fable+</option>
              </select>
              <Button type="submit" disabled={busy} className="justify-self-end">Send report</Button>
            </form>
          )}
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl border p-5 text-sm">
            <LifeBuoy className="h-5 w-5 text-primary" aria-hidden="true" />
            <p className="mt-2 font-semibold">Other ways to reach us</p>
            <p className="mt-1 text-muted-foreground">Email <a className="underline" href="mailto:hello@fableplus.uk">hello@fableplus.uk</a>. In an emergency call 999.</p>
            <ul className="mt-3 space-y-1">
              <li><Link className="underline" to="/accessibility">Accessibility</Link></li>
              <li><Link className="underline" to="/settings#privacy">Privacy settings</Link></li>
              <li><Link className="underline" to="/legal/company">Company details</Link></li>
            </ul>
          </div>
          {user && mine.length ? (
            <div className="rounded-2xl border p-5 text-sm">
              <p className="font-semibold">Your requests</p>
              <ul className="mt-2 divide-y">{mine.slice(0, 8).map((t) => (
                <li key={t.id} className="py-2">
                  {t.conversation_id ? <Link className="font-medium underline" to={"/messages/" + t.conversation_id}>{t.subject}</Link> : <span className="font-medium">{t.subject}</span>}
                  <span className="block text-xs text-muted-foreground">{t.status} · {new Date(t.created_at).toLocaleDateString("en-GB")}</span>
                </li>
              ))}</ul>
            </div>
          ) : null}
        </aside>
      </section>
    </main>
  );
}
